/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * COREX DESIGN SYSTEM — Theme Provider
 * ============================================================================
 * 
 * React context for theme management.
 * Handles theme switching, persistence, and system preference detection.
 * 
 * Usage:
 *   // In main.tsx or App.tsx
 *   import { ThemeProvider } from '@/lib/theme';
 *   
 *   <ThemeProvider defaultTheme="system">
 *     <App />
 *   </ThemeProvider>
 * 
 *   // In components
 *   import { useTheme } from '@/lib/theme';
 *   const { theme, setTheme, toggleTheme, isDark } = useTheme();
 * 
 * ============================================================================
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from 'react';

/* ==========================================================================
   TYPES
   ========================================================================== */

export type ThemeName = 'dark' | 'light' | 'system';
export type ResolvedTheme = 'dark' | 'light';

export interface ThemeContextValue {
  /** Current theme setting (dark, light, or system) */
  theme: ThemeName;
  
  /** Resolved theme (what's actually displayed - dark or light) */
  resolvedTheme: ResolvedTheme;
  
  /** Set theme explicitly */
  setTheme: (theme: ThemeName) => void;
  
  /** Toggle between dark and light */
  toggleTheme: () => void;
  
  /** Whether the resolved theme is dark */
  isDark: boolean;
  
  /** Whether theme is set to follow system */
  isSystem: boolean;

  /** Whether the theme has been loaded from storage */
  isLoaded: boolean;

  /** Whether this is the user's first visit (for tutorial) */
  isFirstVisit: boolean;

  /** Mark the tutorial as completed */
  completeTutorial: () => void;

  /** Reset the tutorial (for demo/testing) */
  resetTutorial: () => void;
}

export interface ThemeProviderProps {
  children: React.ReactNode;
  
  /** Default theme on first visit */
  defaultTheme?: ThemeName;
  
  /** Storage key for persistence */
  storageKey?: string;
  
  /** Force a specific theme (disables switching) */
  forcedTheme?: ThemeName;
}


/* ==========================================================================
   CONSTANTS
   ========================================================================== */

const STORAGE_KEY = 'corex-theme';
const TUTORIAL_KEY = 'corex-theme-tutorial-complete';


/* ==========================================================================
   CONTEXT
   ========================================================================== */

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);


/* ==========================================================================
   HELPER FUNCTIONS
   ========================================================================== */

/**
 * Get the system's preferred color scheme
 */
function getSystemTheme(): ResolvedTheme {
  if (typeof window === 'undefined') return 'dark';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/**
 * Resolve the actual theme from the theme setting
 */
function resolveTheme(theme: ThemeName): ResolvedTheme {
  if (theme === 'system') {
    return getSystemTheme();
  }
  return theme;
}

/**
 * Apply theme to document
 */
function applyTheme(theme: ResolvedTheme): void {
  const root = document.documentElement;
  root.setAttribute('data-theme', theme);
  
  // Also update meta theme-color for mobile browsers
  const metaThemeColor = document.querySelector('meta[name="theme-color"]');
  if (metaThemeColor) {
    metaThemeColor.setAttribute(
      'content',
      theme === 'dark' ? '#0D0F14' : '#F7FAFC'
    );
  }
}


/* ==========================================================================
   THEME SCRIPT (for preventing flash)
   ========================================================================== */

/**
 * Script to inject in <head> to prevent flash of wrong theme
 * This runs before React hydrates
 */
export const themeScript = `
  (function() {
    try {
      var theme = localStorage.getItem('${STORAGE_KEY}');
      var resolved = 'dark';
      
      if (theme === 'system' || !theme) {
        resolved = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      } else if (theme === 'light') {
        resolved = 'light';
      }
      
      document.documentElement.setAttribute('data-theme', resolved);
    } catch (e) {
      document.documentElement.setAttribute('data-theme', 'dark');
    }
  })();
`;

/**
 * Component to inject theme script
 */
export const ThemeScript: React.FC = () => (
  <script dangerouslySetInnerHTML={{ __html: themeScript }} />
);


/* ==========================================================================
   PROVIDER COMPONENT
   ========================================================================== */

export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  defaultTheme = 'system',
  storageKey = STORAGE_KEY,
  forcedTheme,
}) => {
  const [theme, setThemeState] = useState<ThemeName>(defaultTheme);
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() => 
    resolveTheme(defaultTheme)
  );
  const [isLoaded, setIsLoaded] = useState(false);
  const [isFirstVisit, setIsFirstVisit] = useState(false);

  // Load theme from storage on mount
  useEffect(() => {
    if (forcedTheme) {
      setThemeState(forcedTheme);
      setResolvedTheme(resolveTheme(forcedTheme));
      applyTheme(resolveTheme(forcedTheme));
      setIsLoaded(true);
      return;
    }

    try {
      const stored = localStorage.getItem(storageKey) as ThemeName | null;
      const tutorialComplete = localStorage.getItem(TUTORIAL_KEY);
      
      if (stored && ['dark', 'light', 'system'].includes(stored)) {
        setThemeState(stored);
        const resolved = resolveTheme(stored);
        setResolvedTheme(resolved);
        applyTheme(resolved);
      } else {
        // First visit - use system preference
        setIsFirstVisit(true);
        const resolved = resolveTheme('system');
        setResolvedTheme(resolved);
        applyTheme(resolved);
        setThemeState('system');
      }

      // Check if tutorial was never completed
      if (!tutorialComplete) {
        setIsFirstVisit(true);
      }
    } catch (e) {
      // localStorage not available, use default
      const resolved = resolveTheme(defaultTheme);
      setResolvedTheme(resolved);
      applyTheme(resolved);
    }

    setIsLoaded(true);
  }, [forcedTheme, storageKey, defaultTheme]);

  // Listen for system theme changes
  useEffect(() => {
    if (theme !== 'system') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e: MediaQueryListEvent) => {
      const newResolved = e.matches ? 'dark' : 'light';
      setResolvedTheme(newResolved);
      applyTheme(newResolved);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme]);

  // Set theme handler
  const setTheme = useCallback((newTheme: ThemeName) => {
    if (forcedTheme) return;
    
    setThemeState(newTheme);
    const resolved = resolveTheme(newTheme);
    setResolvedTheme(resolved);
    applyTheme(resolved);
    
    try {
      localStorage.setItem(storageKey, newTheme);
    } catch (e) {
      // localStorage not available
    }
  }, [forcedTheme, storageKey]);

  // Toggle between dark and light
  const toggleTheme = useCallback(() => {
    const newTheme = resolvedTheme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
  }, [resolvedTheme, setTheme]);

  // Complete tutorial
  const completeTutorial = useCallback(() => {
    setIsFirstVisit(false);
    try {
      localStorage.setItem(TUTORIAL_KEY, 'true');
    } catch (e) {
      // localStorage not available
    }
  }, []);

  // Reset tutorial (for demo/testing)
  const resetTutorial = useCallback(() => {
    setIsFirstVisit(true);
    try {
      localStorage.removeItem(TUTORIAL_KEY);
    } catch (e) {
      // localStorage not available
    }
  }, []);

  // Memoize context value
  const value = useMemo<ThemeContextValue>(() => ({
    theme: forcedTheme || theme,
    resolvedTheme,
    setTheme,
    toggleTheme,
    isDark: resolvedTheme === 'dark',
    isSystem: theme === 'system',
    isLoaded,
    isFirstVisit,
    completeTutorial,
    resetTutorial,
  }), [
    theme,
    resolvedTheme,
    setTheme,
    toggleTheme,
    isLoaded,
    isFirstVisit,
    completeTutorial,
    resetTutorial,
    forcedTheme,
  ]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};


/* ==========================================================================
   HOOK
   ========================================================================== */

export const useTheme = (): ThemeContextValue => {
  const context = useContext(ThemeContext);
  
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  
  return context;
};


/* ==========================================================================
   EXPORTS
   ========================================================================== */

export default ThemeProvider;
